import { Link } from 'react-router-dom';
import { platforms } from '@/mocks/suviData';
import SEOHead from '@/components/base/SEOHead';

export default function Plataformas() {
  return (
    <>
      <SEOHead
        title="Plataformas Digitales para Agencias de Viaje | Suvi Digital"
        description="Plataformas digitales que utilizamos para potenciar agencias de viaje: Shopify, WordPress, HubSpot, Meta Ads, Google Analytics y más."
        keywords="plataformas marketing digital, herramientas agencia viaje, Shopify turismo, HubSpot travel, Google Analytics turismo"
        canonical="https://suvidigital.com/plataformas"
        schemaType="WebPage"
      />
      <main className="min-h-screen">
        {/* Hero */}
        <section className="relative min-h-[420px] md:min-h-[500px] flex items-center overflow-hidden">
          <div className="absolute inset-0 animate-gradient bg-gradient-to-br from-primary-100 via-background-50 to-secondary-100" />
          <div className="absolute top-20 right-20 w-72 h-72 bg-primary-200/30 rounded-full blur-3xl animate-float-slow" />
          <div className="absolute bottom-20 left-10 w-64 h-64 bg-accent-200/20 rounded-full blur-3xl animate-float-delay-1" />
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{ backgroundImage: `radial-gradient(circle, oklch(var(--foreground-900)) 1px, transparent 1px)`, backgroundSize: '40px 40px' }}
          />

          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 xl:px-16 pt-24 md:pt-28">
            <div className="max-w-4xl">
              <span className="inline-block bg-primary-50 text-primary-600 text-sm font-semibold px-4 py-1.5 rounded-full mb-5 border border-primary-200/50">
                Tecnología
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground-900 mb-5 leading-tight">
                Las mejores{' '}
                <span className="text-gradient">plataformas</span>{' '}
                para tu agencia
              </h1>
              <p className="text-foreground-500 text-lg md:text-xl leading-relaxed max-w-2xl">
                Utilizamos las herramientas líderes en el mercado para crear soluciones robustas, escalables y profesionales.
              </p>
            </div>
          </div>
        </section>

        {/* Platforms Grid */}
        <section className="bg-white py-20 md:py-28">
          <div className="px-4 md:px-6 lg:px-10 xl:px-16">
            <div className="text-center max-w-3xl mx-auto mb-14 md:mb-20">
              <span className="inline-block bg-primary-50 text-primary-600 text-sm font-semibold px-4 py-1.5 rounded-full mb-4 border border-primary-200/50">
                Stack Tecnológico
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-5">
                Tecnologías que{' '}
                <span className="text-gradient">impulsan</span>{' '}
                resultados
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {platforms.map((platform, i) => (
                <div
                  key={platform.name}
                  className="card-lift bg-background-50 border border-background-200/60 rounded-2xl p-6 group"
                  style={{ animationDelay: `${i * 100}ms` }}
                >
                  <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br from-primary-100 to-secondary-100 text-primary-600 mb-4 group-hover:scale-110 transition-transform duration-300">
                    <i className={`${platform.icon} text-2xl`} />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-foreground-900 mb-2">{platform.name}</h3>
                  <p className="text-foreground-500 text-sm leading-relaxed mb-3">{platform.description}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {platform.category.map((cat) => (
                      <span key={cat} className="bg-background-100 text-foreground-500 text-xs font-medium px-2.5 py-1 rounded-full border border-background-200/50">
                        {cat}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20 md:py-28 overflow-hidden">
          <div className="absolute inset-0 animate-gradient bg-gradient-to-r from-primary-700 via-primary-600 to-secondary-600" />
          <div className="absolute top-10 left-[20%] w-56 h-56 bg-white/5 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-10 right-[15%] w-72 h-72 bg-accent-500/10 rounded-full blur-3xl animate-float-delay-1" />

          <div className="relative z-10 px-4 md:px-6 lg:px-10 xl:px-16">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5">
                ¿Quieres saber qué plataformas{' '}
                <span className="text-accent-300">se adaptan</span>{' '}
                a tu agencia?
              </h2>
              <p className="text-white/70 text-base md:text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
                Cada agencia tiene necesidades diferentes. Te asesoramos en la elección de las herramientas ideales para tu negocio.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contacto"
                  className="btn-glow bg-accent-500 hover:bg-accent-600 text-white font-semibold px-8 py-4 rounded-xl text-center transition-colors whitespace-nowrap text-lg"
                >
                  Consulta Gratuita
                </Link>
                <a
                  href="https://wa.me/51989307864"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/15 hover:bg-white/25 text-white font-semibold px-8 py-4 rounded-xl text-center transition-colors border border-white/30 whitespace-nowrap text-lg"
                >
                  <i className="ri-whatsapp-line mr-2" /> WhatsApp
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}